import React, { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, MapPin, Mail } from "lucide-react";
import { submitContact } from "../lib/api";
import SEO from "../components/SEO";
import ShareButtons from "../components/ShareButtons";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message || "Sent.");
      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Try again soon.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-[#FDFBF7]">
      <SEO
        title="Contact — Drop us a postcard"
        description="Story tips, friendly notes, and travel recommendations welcome. We read everything and write back from the road."
        path="/contact"
      />
      <section className="pt-36 pb-16 lg:pt-44 lg:pb-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="kicker">Say Hello</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[0.98] mt-5">
              Drop us a<br /><em className="italic">postcard.</em>
            </h1>
            <p className="mt-6 text-stone-600 leading-relaxed text-[15px] max-w-md">
              Questions, story tips, a recommendation for a hill-top village we haven't heard of — we read everything, and we write back from the road.
            </p>

            <div className="mt-12 space-y-6 border-t border-stone-300 pt-8">
              <div className="flex items-start gap-4">
                <Mail size={18} className="text-[#9c4122] mt-0.5" />
                <div>
                  <p className="kicker text-stone-500">Email</p>
                  <p className="text-stone-800 mt-1">hello@globezing.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-[#9c4122] mt-0.5" />
                <div>
                  <p className="kicker text-stone-500">Currently parked in</p>
                  <p className="text-stone-800 mt-1">A rented apartment, somewhere with good bread.</p>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <ShareButtons title="Contact Globezing" description="Say hello — we read everything." />
            </div>
          </div>

          <div className="md:col-span-7">
            {sent ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="contact-success" className="border border-stone-300 p-10 bg-white">
                <h3 className="font-serif text-3xl text-stone-900">Your note is on the way.</h3>
                <p className="mt-4 text-stone-600 leading-relaxed">We'll reply from wherever the road finds us.</p>
                <button onClick={() => setSent(false)} data-testid="contact-send-another" className="mt-6 text-sm tracking-[0.2em] uppercase font-semibold text-[#9c4122] link-underline">Send another</button>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8 pt-2">
                <label className="block">
                  <span className="kicker text-stone-500">Your name</span>
                  <input type="text" value={form.name} onChange={update("name")} data-testid="contact-name-input" className="input-editorial mt-2" placeholder="Your name" required />
                </label>
                <label className="block">
                  <span className="kicker text-stone-500">Email</span>
                  <input type="email" value={form.email} onChange={update("email")} data-testid="contact-email-input" className="input-editorial mt-2" placeholder="you@somewhere.com" required />
                </label>
                <label className="block">
                  <span className="kicker text-stone-500">Your message</span>
                  <textarea value={form.message} onChange={update("message")} rows={6} data-testid="contact-message-input" className="input-editorial mt-2 resize-none" placeholder="Write us a postcard..." required />
                </label>
                <button type="submit" disabled={loading} data-testid="contact-submit-button" className="group inline-flex items-center gap-3 bg-stone-900 text-[#FDFBF7] px-8 py-4 text-[12px] tracking-[0.25em] uppercase font-semibold hover:bg-[#9c4122] transition-colors disabled:opacity-50">
                  {loading ? "Sending..." : "Send message"}
                  <Send size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
